import React, { useEffect } from "react";
import { I } from "./icons";
import logoImg from "@/assets/nutrimarket-logo.png?w=96&format=webp";
import { useNav } from "./context/NavContext";
import type { PrefetchFn } from "./types";

export function AppSidebar({
  sideOpen, lang, setLang, t, user, signOut, userRole,
  rewardPts, cmpIds, agentAlerts,
  prefetchDashboard, prefetchProducts
}: {
  sideOpen: boolean; lang: string;
  setLang: (fn: (l: string) => string) => void; t: Record<string, string>;
  user: any; signOut: () => void; userRole: string | null;
  rewardPts: number; cmpIds: any[]; agentAlerts: any[];
  prefetchDashboard?: PrefetchFn; prefetchProducts?: PrefetchFn;
}) {
  const { page, nav } = useNav();
  useEffect(() => {
    if (sideOpen) {
      prefetchDashboard?.();
      prefetchProducts?.();
    }
  }, [sideOpen, prefetchDashboard, prefetchProducts]);

  const navItems = [
    { id: "dashboard", ic: I.home, lb: t.dashboard },
    { id: "products", ic: I.grid, lb: t.products },
    // { id: "stores", ic: I.store, lb: t.stores }, // hidden — pending rework
    { id: "compare", ic: I.compare, lb: `${t.compare}${cmpIds.length ? ` (${cmpIds.length})` : ""}` },
  ];
  const featItems = [
    { id: "nutricoach", ic: I.spark, lb: "🌿 NutriCoach AI" },
    { id: "bodyScan", ic: I.bodyScan, lb: t.bodyScan },
    { id: "wearable", ic: I.watch, lb: t.wearable },
    { id: "community", ic: I.users2, lb: t.community },
    { id: "aiAgent", ic: I.cpu, lb: t.aiAgent },
    { id: "scanner", ic: I.barcode, lb: t.scanner },
    { id: "tracker", ic: I.clock, lb: t.tracker },
    { id: "learn", ic: I.spark, lb: t.learn },
    { id: "healthReport", ic: I.trendUp, lb: t.healthReport },
  ];
  const accItems = [
    { id: "myaccount", ic: I.user, lb: lang === "es" ? "Mi Cuenta" : "My Account" },
    { id: "orders", ic: I.pkg, lb: t.orders },
    { id: "favorites", ic: I.heart, lb: t.favorites },
    { id: "rewards", ic: I.gift, lb: `${t.rewards} (${rewardPts})` },
    { id: "referral", ic: I.share, lb: t.referral },
    { id: "profile", ic: I.user, lb: t.profile },
  ];

  return (
    <aside className={`si ${sideOpen ? "mob" : ""}`} aria-label={lang === "es" ? "Navegación principal" : "Primary navigation"}>
      <div className="si-logo">
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <img src={logoImg} alt="" width={32} height={32} style={{ objectFit: "contain" }} />
          <h1>
            Nutri<span>Market</span>
            <span className="sr-only"> — {lang === "es" ? "Marketplace de Bienestar en RD" : "Wellness Marketplace in DR"}</span>
          </h1>
        </div>
        <p>{lang === "es" ? "Marketplace de Bienestar" : "Wellness Marketplace"}</p>
      </div>
      <nav className="si-nav" aria-label={lang === "es" ? "Secciones" : "Sections"}>
        {navItems.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`ni ${page === n.id ? "on" : ""}`}
            onClick={() => nav(n.id)}
            onMouseEnter={n.id === "dashboard" ? prefetchDashboard : n.id === "products" ? prefetchProducts : undefined}
            onTouchStart={n.id === "dashboard" ? prefetchDashboard : n.id === "products" ? prefetchProducts : undefined}
            aria-current={page === n.id ? "page" : undefined}
          >
            <span aria-hidden="true">{n.ic}</span>{n.lb}
          </button>
        ))}
        <div className="ns" role="presentation">{t.features}</div>
        {featItems.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`ni ${page === n.id ? "on" : ""}`}
            onClick={() => nav(n.id)}
            aria-current={page === n.id ? "page" : undefined}
          >
            <span aria-hidden="true">{n.ic}</span>{n.lb}
          </button>
        ))}
        <div className="ns" role="presentation">{t.account}</div>
        {accItems.map((n) => (
          <button
            key={n.id}
            type="button"
            className={`ni ${page === n.id ? "on" : ""}`}
            onClick={() => nav(n.id)}
            aria-current={page === n.id ? "page" : undefined}
          >
            <span aria-hidden="true">{n.ic}</span>{n.lb}
          </button>
        ))}
        {userRole === "admin" && (
          <>
            <div className="ns" role="presentation">Admin</div>
            <button
              type="button"
              className={`ni ${page === "admin" ? "on" : ""}`}
              onClick={() => nav("admin")}
              aria-current={page === "admin" ? "page" : undefined}
            >
              <span aria-hidden="true">{I.shield}</span>{t.adminPanel}
            </button>
          </>
        )}
        {userRole === "vendor" && (
          <>
            <div className="ns" role="presentation">Vendor</div>
            <button
              type="button"
              className={`ni ${page === "vendor" ? "on" : ""}`}
              onClick={() => nav("vendor")}
              aria-current={page === "vendor" ? "page" : undefined}
            >
              <span aria-hidden="true">{I.store}</span>{t.vendorPanel}
            </button>
          </>
        )}
        {user && userRole !== "vendor" && userRole !== "admin" && (
          <button
            type="button"
            className={`ni ${page === "vendorApply" ? "on" : ""}`}
            onClick={() => nav("vendorApply")}
            aria-current={page === "vendorApply" ? "page" : undefined}
            title={lang === "es" ? "Conviértete en vendedor" : "Become a vendor"}
          >
            <span aria-hidden="true">{I.store}</span>
            {lang === "es" ? "Vender en NutriMart" : "Sell on NutriMart"}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button
          type="button"
          className="lt"
          onClick={() => setLang((l) => (l === "en" ? "es" : "en"))}
          aria-label={lang === "es" ? "Cambiar idioma a Inglés" : "Switch language to Spanish"}
        >
          <span aria-hidden="true">{I.globe}</span><span className="al">{lang.toUpperCase()}</span>·{lang === "en" ? "🇩🇴 Español" : "🇺🇸 English"}
        </button>
        <button type="button" className="ni" onClick={() => signOut()} aria-label={t.signOut}>
          <span aria-hidden="true">{I.logout}</span>{t.signOut}
        </button>
      </nav>
      <div className="si-ft">
        <div className="si-u">
          <div className="si-av">
            {user?.user_metadata?.first_name?.[0]?.toUpperCase() || user?.email?.[0]?.toUpperCase() || "U"}
          </div>
          <div className="si-ui">
            <b>
              {user?.user_metadata?.first_name
                ? `${user.user_metadata.first_name} ${user.user_metadata.last_name || ""}`.trim()
                : user?.email || "User"}
            </b>
            <span>{t.premiumMember}</span>
          </div>
        </div>
      </div>
    </aside>
  );
}